import React, { useId } from 'react';
import { Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

export const LICENSE_FIELD_HELP = {
  status: 'Controls the license lifecycle. Suspended and cancelled deny access immediately; grace and expired apply the behaviors selected below.',
  starts: 'The date and time when this license becomes valid. Times are entered in your browser’s local timezone and stored securely in UTC.',
  expires: 'The date and time when normal trial or active access ends. It must be later than the start time.',
  graceEnds: 'Optional final date and time for grace access. It cannot be earlier than the license expiration.',
  seats: 'The maximum number of named people in this institution. One person uses one seat whether they use web, mobile, or both.',
  graceBehavior: 'The access level applied while the license status is Grace, until Grace ends.',
  expiredBehavior: 'The access level applied after expiration or after the grace period ends. Deny login is the safest default.',
};

export const LICENSE_FIELD_HELP_AR = {
  status: 'يتحكم في دورة حياة الترخيص. يمنع الإيقاف والإلغاء الوصول فورًا، بينما تطبّق فترة السماح والانتهاء السلوك المحدد أدناه.',
  starts: 'التاريخ والوقت اللذان يبدأ عندهما سريان الترخيص. يُدخل الوقت حسب منطقة المتصفح الزمنية ويُحفظ بصيغة UTC.',
  expires: 'التاريخ والوقت اللذان ينتهي عندهما الوصول التجريبي أو النشط. يجب أن يكون بعد وقت البدء.',
  graceEnds: 'وقت اختياري لانتهاء فترة السماح. لا يمكن أن يسبق تاريخ انتهاء الترخيص.',
  seats: 'الحد الأقصى للأشخاص المسمّين في هذه المؤسسة. يستخدم الشخص مقعدًا واحدًا سواء استخدم الويب أو الهاتف أو كليهما.',
  graceBehavior: 'مستوى الوصول المطبق عندما تكون حالة الترخيص في فترة السماح وحتى انتهائها.',
  expiredBehavior: 'مستوى الوصول بعد انتهاء الترخيص أو فترة السماح. منع الدخول هو الخيار الافتراضي الأكثر أمانًا.',
};

export const BREAK_GLASS_HELP = {
  section: 'Use only for a genuine operational emergency when the ordinary institution-approved support process cannot be completed in time. Only a SaaS superadmin can activate it.',
  writeAccess: 'This is not read-only support. The selected Platform Support user receives temporary operational write access, and the institution receives a visible critical security notice.',
  expiry: 'Choose the shortest necessary duration. The system rejects past times and any duration longer than four hours.',
  activate: 'Activation is immediate. It records the support user, institution, ticket, reason, authorizer and expiration in the audit trail. Revoke the grant as soon as the emergency ends.',
};

export const BREAK_GLASS_HELP_AR = {
  section: 'يُستخدم فقط في طوارئ تشغيلية حقيقية عندما يتعذر إكمال إجراء الدعم العادي المعتمد من المؤسسة في الوقت المناسب. ولا يفعّله إلا مسؤول منصة SaaS.',
  writeAccess: 'هذا ليس دعمًا للقراءة فقط. يحصل مستخدم دعم المنصة المحدد على وصول تشغيلي مؤقت للكتابة، وتتلقى المؤسسة تنبيهًا أمنيًا حرجًا وواضحًا.',
  expiry: 'اختر أقصر مدة لازمة. يرفض النظام الأوقات الماضية وأي مدة تتجاوز أربع ساعات.',
  activate: 'يبدأ التفعيل فورًا ويسجل مستخدم الدعم والمؤسسة والمرجع والسبب والمخوّل ووقت الانتهاء في سجل التدقيق. ألغِ التفويض فور انتهاء الطوارئ.',
};

export const InfoTip = ({ label, content }) => {
  const descriptionId = useId();
  const isArabic = /[\u0600-\u06ff]/.test(String(label || ''));
  return (
    <span className="inline-flex shrink-0 align-middle">
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={isArabic ? `معلومات عن ${label}` : `Information about ${label}`}
              aria-describedby={descriptionId}
              title={content}
              onClick={(event) => event.stopPropagation()}
              className="saas-info-tip-trigger inline-flex h-5 w-5 items-center justify-center rounded-full transition-colors focus:outline-none"
            >
              <Info aria-hidden="true" className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="saas-info-tip-content z-[100] max-w-xs text-sm leading-5">
            {content}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <span id={descriptionId} className="sr-only">{content}</span>
    </span>
  );
};
